import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserCog, Plus, Trash2, Eye, EyeOff, Edit } from 'lucide-react';

export default function CarwashOperarios() {
  const [operarios, setOperarios] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: '', email: '', password: '1234' });
  const [cargando, setCargando] = useState(false);
  const [mostrarPwd, setMostrarPwd] = useState(false);

  const cargar = useCallback(async () => {
    try { const res = await api.get('/carwash/operarios'); setOperarios(res.data); }
    catch { toast.error('Error al cargar operarios'); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (!editando && !form.email.trim()) { toast.error('El email es obligatorio'); return; }
    setCargando(true);
    try {
      if (editando) {
        await api.put(`/carwash/operarios/${editando}`, form);
        toast.success('Operario actualizado');
      } else {
        await api.post('/carwash/operarios', form);
        toast.success('Operario creado');
      }
      setDialog(false);
      setEditando(null);
      setForm({ nombre: '', email: '', password: '1234' });
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setCargando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Eliminar este operario?')) return;
    try { await api.delete(`/carwash/operarios/${id}`); toast.success('Operario eliminado'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };

  const toggleActivo = async (op) => {
    try { await api.put(`/carwash/operarios/${op.id}`, { activo: !op.activo }); cargar(); }
    catch { toast.error('Error al actualizar'); }
  };

  return (
    <div className="space-y-4" data-testid="carwash-operarios-page">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-100 font-barlow flex items-center gap-2">
          <UserCog className="w-5 h-5 text-cyan-400" /> Operarios / Lavadores
        </h2>
        <Button onClick={() => { setEditando(null); setForm({ nombre: '', email: '', password: '1234' }); setDialog(true); }}
          className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="nuevo-operario-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Operario
        </Button>
      </div>

      <p className="text-xs text-slate-500">
        Los operarios pueden ver los turnos del dia y cambiar su estado desde su celular. No tienen acceso a pagos, configuracion ni datos sensibles.
      </p>

      {operarios.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-8 text-center">
            <UserCog className="w-10 h-10 text-slate-700 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No hay operarios registrados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {operarios.map(op => (
            <Card key={op.id} className="bg-slate-900/40 border-slate-800" data-testid={`operario-card-${op.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-200">{op.nombre}</p>
                  <p className="text-xs text-slate-500">{op.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={op.activo ? 'bg-emerald-500/20 text-emerald-400 border-emerald-700' : 'bg-red-500/20 text-red-400 border-red-700'}
                    onClick={() => toggleActivo(op)} style={{ cursor: 'pointer' }}>
                    {op.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => { setEditando(op.id); setForm({ nombre: op.nombre, email: op.email, password: '' }); setDialog(true); }}
                    className="text-slate-400 hover:text-slate-200" data-testid={`editar-operario-${op.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => eliminar(op.id)}
                    className="text-slate-400 hover:text-red-400" data-testid={`eliminar-operario-${op.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-100">{editando ? 'Editar Operario' : 'Nuevo Operario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
              <Input value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" data-testid="operario-nombre-input" /></div>
            {!editando && (
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200 mt-1" data-testid="operario-email-input" /></div>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">{editando ? 'Nueva contrasena (dejar vacio para no cambiar)' : 'Contrasena'}</Label>
              <div className="relative mt-1">
                <Input type={mostrarPwd ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200 pr-10" data-testid="operario-password-input" />
                <button type="button" onClick={() => setMostrarPwd(!mostrarPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{mostrarPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <Button onClick={guardar} disabled={cargando}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="guardar-operario-btn">
              {cargando ? 'Guardando...' : editando ? 'Actualizar' : 'Crear Operario'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
